// ---------------------------------------------------------------------------
// Course Component: Drip Gate — Conditional Content Lock
// ---------------------------------------------------------------------------
// Blocks access to content below it until a condition is met.
// 5 drip modes: immediately, date-based, days-after-enrollment,
// progress-based, quiz-score-based.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

export const DRIP_MODES = [
  "immediately",
  "date_based",
  "days_after_enrollment",
  "progress_based",
  "quiz_score_based",
] as const;

export type DripMode = (typeof DRIP_MODES)[number];

export interface DripGateProps {
  mode: DripMode;
  /** For date_based: ISO date string */
  unlockDate?: string;
  /** For days_after_enrollment: number of days */
  delayDays?: number;
  /** For progress_based: required module/lesson ID to complete */
  prerequisiteId?: string;
  prerequisiteType?: "module" | "lesson";
  /** For quiz_score_based: required quiz ID + minimum score */
  requiredQuizId?: string;
  requiredScore?: number;
  /** Message shown when locked */
  lockedMessage?: string;
  /** Message shown when unlocked */
  unlockedMessage?: string;
}

export interface DripEvalContext {
  enrolledAt: Date;
  currentDate: Date;
  completedLessonIds: string[];
  completedModuleIds: string[];
  quizScores: Record<string, number>; // quizId → score
}

/**
 * Evaluate whether a drip gate should be unlocked.
 */
export function evaluateDripGate(
  props: DripGateProps,
  ctx: DripEvalContext,
): { unlocked: boolean; reason: string } {
  switch (props.mode) {
    case "immediately":
      return { unlocked: true, reason: "Available immediately" };

    case "date_based": {
      if (!props.unlockDate) return { unlocked: true, reason: "No unlock date set" };
      const unlockDate = new Date(props.unlockDate);
      const isUnlocked = ctx.currentDate >= unlockDate;
      return {
        unlocked: isUnlocked,
        reason: isUnlocked ? "Unlock date reached" : `Unlocks on ${props.unlockDate}`,
      };
    }

    case "days_after_enrollment": {
      const days = props.delayDays ?? 0;
      const enrolledDate = ctx.enrolledAt;
      const unlockDate = new Date(enrolledDate.getTime() + days * 24 * 60 * 60 * 1000);
      const isUnlocked = ctx.currentDate >= unlockDate;
      return {
        unlocked: isUnlocked,
        reason: isUnlocked ? "Time requirement met" : `Unlocks ${days} days after enrollment`,
      };
    }

    case "progress_based": {
      if (!props.prerequisiteId) return { unlocked: true, reason: "No prerequisite set" };
      const completedIds = props.prerequisiteType === "module"
        ? ctx.completedModuleIds
        : ctx.completedLessonIds;
      const isUnlocked = completedIds.includes(props.prerequisiteId);
      return {
        unlocked: isUnlocked,
        reason: isUnlocked ? "Prerequisite completed" : `Complete ${props.prerequisiteType ?? "lesson"} to unlock`,
      };
    }

    case "quiz_score_based": {
      if (!props.requiredQuizId) return { unlocked: true, reason: "No quiz requirement set" };
      const score = ctx.quizScores[props.requiredQuizId];
      const required = props.requiredScore ?? 70;
      const isUnlocked = score !== undefined && score >= required;
      return {
        unlocked: isUnlocked,
        reason: isUnlocked
          ? `Quiz passed (${score}%)`
          : score !== undefined
            ? `Quiz score ${score}% — need ${required}%`
            : `Complete quiz with ${required}%+ to unlock`,
      };
    }

    default:
      return { unlocked: false, reason: "Unknown drip mode" };
  }
}

export const DRIP_GATE_DEF = {
  type: "drip-gate" as const,
  label: "Drip Gate",
  category: "course",
  defaultProps: {
    mode: "immediately" as const,
    lockedMessage: "Complete the previous section to continue.",
    unlockedMessage: "Content unlocked!",
  } satisfies Partial<DripGateProps>,
};

function describeGateMode(props: DripGateProps): string {
  switch (props.mode) {
    case "date_based":
      return props.unlockDate ? `Unlocks on ${props.unlockDate}` : "Unlock date not configured";
    case "days_after_enrollment":
      return `Unlocks ${props.delayDays ?? 0} day(s) after enrollment`;
    case "progress_based":
      return `Requires ${props.prerequisiteType ?? "lesson"} ${props.prerequisiteId ?? "completion"}`;
    case "quiz_score_based":
      return `Requires ${props.requiredScore ?? 70}% on quiz ${props.requiredQuizId ?? ""}`.trim();
    default:
      return "Available immediately";
  }
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as DripGateProps;
  const statusMessage = p.lockedMessage?.trim() || "This section unlocks later.";

  return `<section class="ease-section ease-course-drip-gate" data-mode="${attr(p.mode)}">
  <div class="ease-course-drip-gate__card">
    <p class="ease-course-drip-gate__eyebrow">Course gate</p>
    <h2 class="ease-course-drip-gate__title">${text(statusMessage)}</h2>
    <p class="ease-course-drip-gate__detail">${text(describeGateMode(p))}</p>
    ${p.unlockedMessage ? `<p class="ease-course-drip-gate__hint">${text(p.unlockedMessage)}</p>` : ""}
  </div>
</section>`;
}

registerRenderer("drip-gate", { render });
